/* ═══════════════════════════════════════════════════════════
   ASTROITE — Site content localization

   localizePeeth(record, lang) / localizeJyotirlinga(record, lang)
   return a shallow copy of the DB record with its content fields
   swapped to the chosen language. Always fallback-safe: an
   untranslated field renders its native DB value rather than
   breaking or showing blanks.

   Data formats differ between the two datasets:
     • Peeth name/shakti_name/bhairava_name/body_part are stored
       bilingual ("देवनागरी | English") — split here, no pack needed.
       Peeth history/significance are English (native = en).
     • Jyotirlinga name/shiva_form/history/significance are Hindi
       (native = hi); English/Marathi come from the pack.
   ═══════════════════════════════════════════════════════════ */

import { PEETH_TEXT, JYOTIRLINGA_TEXT } from './sitesContent';

// Canonical totals (DB: 51 + 57 peeths, 12 jyotirlingas).
export const TOTAL_PEETHS = 108;
export const TOTAL_JYOTIRLINGAS = 12;

/* Split a "देवनागरी | English" value. Devanagari side serves hi & mr
   (proper nouns are written identically in Marathi); English side
   serves en. Non-bilingual strings are returned unchanged. */
function pickBilingual(value, lang) {
  if (typeof value !== 'string') return value;
  const sep = value.indexOf('|');
  if (sep === -1) return value;
  const deva = value.slice(0, sep).trim();
  const en = value.slice(sep + 1).trim();
  return lang === 'en' ? (en || deva) : (deva || en);
}

/* Native language uses the DB value as-is; other languages use the
   content pack, falling back to the DB value when absent. */
function pickField(pack, key, dbValue, lang, nativeLang) {
  if (lang === nativeLang) return dbValue;
  const entry = pack && pack[key];
  if (entry && entry[lang]) return entry[lang];
  return dbValue;
}

export function localizePeeth(p, lang) {
  if (!p) return p;
  const pack = PEETH_TEXT[p.peeth_id];
  return {
    ...p,
    name:          pickBilingual(p.name, lang),
    shakti_name:   pickBilingual(p.shakti_name, lang),
    bhairava_name: pickBilingual(p.bhairava_name, lang),
    body_part:     pickBilingual(p.body_part, lang),
    history:       pickField(pack, 'history', p.history, lang, 'en'),
    significance:  pickField(pack, 'significance', p.significance, lang, 'en'),
  };
}

export function localizeJyotirlinga(j, lang) {
  if (!j) return j;
  const pack = JYOTIRLINGA_TEXT[j.jyotirlinga_id];
  return {
    ...j,
    name:         pickField(pack, 'name', j.name, lang, 'hi'),
    shiva_form:   pickField(pack, 'shivaForm', j.shiva_form, lang, 'hi'),
    history:      pickField(pack, 'history', j.history, lang, 'hi'),
    significance: pickField(pack, 'significance', j.significance, lang, 'hi'),
  };
}
